import { FlexRow } from '@/components/common/Flex';
import { toDollarsWithCents } from '@/util/currency';
import { Box, BoxProps, FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    PlentyGoalAccountFinancialEntity,
    transactionSchedulesFromGoalAccount,
} from '@common/financialEntities';
import { GoalTransaction } from '@common/goals';
import { TransactionScheduleFieldsFragment } from '@common/graphql/generated';
import { getGoalAccountGoalTransactions } from '@common/react/goals';
import { DepositOrWithdrawal } from '@common/react/transactions';
import styleguide from '@common/styleguide';
import { isTerminalAppTransactionStatus } from '@common/transactions';
import { sortBy } from '@common/util/arrays';
import { dt } from '@common/util/dateTime';
import { FC } from 'react';
import { hoverStyleProps } from '../common/styleHelpers';
import { Text } from '../common/Typography';
import { showGoalScheduledTransactionStatusModal } from './GoalScheduledTransactionStatusModal';
import { showGoalTransactionStatusModal } from './GoalTransactionStatusModal';

export interface GoalDetailTransactionsPillCarouselProps extends FlexProps {
    goalAccount: PlentyGoalAccountFinancialEntity | undefined;
}

const GoalDetailTransactionsPillCarousel: FC<
    GoalDetailTransactionsPillCarouselProps
> = ({ goalAccount, ...props }) => {
    const items: CarouselItemProps[] = [];

    if (goalAccount) {
        // Add item props for active transaction schedules with a transaction
        // upcoming in the next 7 days.
        transactionSchedulesFromGoalAccount(goalAccount).forEach((ts) => {
            if (
                ts.amount > 0 &&
                // Upcoming contribution pill should only be shown if it's in the next 7 days
                dt(ts.nextTransactionDate).diff(dt(), 'days') <= 7
            ) {
                const itemProps = getTransactionItemProps(ts, goalAccount);
                if (itemProps) items.push(itemProps);
            }
        });

        // Add item props for non-terminal goal transactions
        getGoalAccountGoalTransactions(goalAccount)
            .filter((txn) => !isTerminalAppTransactionStatus(txn.status))
            .forEach((txn) => {
                const itemProps = getTransactionItemProps(txn, goalAccount);
                if (itemProps) items.push(itemProps);
            });
    }

    return (
        <FlexRow {...props} position="relative" overflow="hidden" mr={-8}>
            <FlexRow overflowX="auto" px={8} gap={2}>
                {sortBy(
                    items,
                    (item) => item.upcomingDate ?? dt().toDateStr()
                ).map((itemProps) => {
                    return <CarouselItem {...itemProps} key={itemProps.key} />;
                })}
            </FlexRow>
            <Box
                boxShadow={`0 0 20px 20px ${styleguide.colors.white}`}
                position="absolute"
                top={0}
                left={0}
                h="full"
                zIndex={3}
            />
            <Box
                boxShadow={`0 0 20px 20px ${styleguide.colors.white}`}
                position="absolute"
                top={0}
                right={0}
                h="full"
                zIndex={3}
            />
        </FlexRow>
    );
};
export default GoalDetailTransactionsPillCarousel;

interface CarouselItemProps extends BoxProps {
    key: string;
    amount: number;
    direction: DepositOrWithdrawal;
    upcomingDate?: string;
    onClick?: VoidFunction;
}

const CarouselItem: FC<CarouselItemProps> = ({
    amount,
    direction,
    upcomingDate,
    onClick,
    ...props
}) => {
    const amountStr = `${
        direction === 'DEPOSIT' ? '+' : '-'
    }${toDollarsWithCents(amount)}`;
    const isInProgress = !upcomingDate;
    const amountColor =
        !isInProgress || direction === 'WITHDRAWAL' ? 'textMuted' : 'green.600';

    const timingStr = isInProgress
        ? 'in progress'
        : 'on ' + dt(upcomingDate).format('MMM D');

    return (
        <FlexRow
            borderColor="border"
            borderWidth={1}
            rounded="full"
            whiteSpace="nowrap"
            px={4}
            py={2}
            gap={1}
            onClick={onClick}
            {...(onClick
                ? hoverStyleProps({
                      borderColor: styleguide.colors.grayLighter,
                  })
                : {})}
            {...props}
        >
            <Text fontSize="sm" color={amountColor}>
                {amountStr}
            </Text>
            <Text fontSize="sm" color="gray.400">
                {timingStr}
            </Text>
        </FlexRow>
    );
};

const getTransactionItemProps = (
    t: GoalTransaction | TransactionScheduleFieldsFragment,
    goalAccount: PlentyGoalAccountFinancialEntity
): CarouselItemProps | undefined => {
    if (t.__typename === 'TransactionSchedule') {
        if (!t.sourceId) return;
        const sourceId = t.sourceId;

        return {
            key: t.id,
            amount: t.amount,
            direction:
                t.destinationId === goalAccount.id ? 'DEPOSIT' : 'WITHDRAWAL',
            upcomingDate: dt(t.nextTransactionDate).toDateStr(),
            onClick: () => {
                assert(
                    goalAccount.goalId,
                    'Goal account must have a goal id to show transaction status modal'
                );
                showGoalScheduledTransactionStatusModal({
                    sourceId,
                    destinationId: t.destinationId,
                    transactionSchedule: t,
                    goalId: goalAccount.goalId,
                });
            },
        };
    } else {
        return {
            key: t.id,
            amount: t.amount,
            direction: t.direction,
            upcomingDate:
                t.plannedProcessingDate &&
                dt(t.plannedProcessingDate).isAfter(dt(), 'day')
                    ? dt(t.plannedProcessingDate).toDateStr()
                    : undefined,
            onClick: () => {
                showGoalTransactionStatusModal({
                    transactionId: t.id,
                    transactionDirection: t.direction,
                });
            },
        };
    }
};
